import {
  IsNumber, IsOptional, IsString,
} from 'class-validator';
import { eventsOfInterest } from './Patients.constants';

export interface IPatient {
  patientId: string;
  publicSubjectId: string;
  zccSubjectId: string;
  internalId: string;
  lmSubjectId: number;
  lmSubjectCode: string;
  sex: 'Male' | 'Female' | 'Multi' | 'Unknown';
  germlineAberration: string;
  ageAtDiagnosis: number;
  ageAtDeath: number;
  ageAtEnrolment: number | null;
  vitalStatus: 'Alive' | 'Dead' | 'Unknown';
  hospital: string;
  enrolmentDate: string;
  registrationDate: string;
  status: string | null;
  stage: string | null;
  comments: string;
}

export interface IPatientHistory {
  clinicalHistory: string;
}

export interface IUpdatePatientBody {
  comments?: string;
}

export interface IUpdatePatientHistoryBody {
  clinicalHistory?: string;
}

export type IPatientFilters = Partial<Pick<IPatient, 'patientId' | 'publicSubjectId' | 'lmSubjectId'>>;

export class UpdatePatientBodyDTO implements IUpdatePatientBody {
  @IsString()
  @IsOptional()
    comments?: string;
}

export class UpdatePatientHistoryBodyDTO implements IUpdatePatientHistoryBody {
  @IsString()
  @IsOptional()
    clinicalHistory?: string;
}

export class PatientFiltersDTO implements IPatientFilters {
  @IsOptional()
  @IsString()
    patientId?: string;

  @IsOptional()
  @IsString()
    publicSubjectId?: string;

  @IsOptional()
  @IsNumber()
    lmSubjectId?: number;
}

export interface IRemainingPatient {
  patientId: string;
  c1SubectId: string;
  c1NewSubectId: string;
  status: string | null;
}

export type PatientEvent = typeof eventsOfInterest[number];

export interface IEvent {
  eventNumber: string;
  event: PatientEvent;
  date: string;
  otherClinicalScenarios?: string;
}

export interface IConfirmedDiagnosisResp {
  pathologist?: string | null;
  histologicalDiagnosis?: string | null;
}

export interface IPatientC1Data {
  oncologistName: string | null;
  oncologistEmail: string | null;
  pathologistName: string | null;
  pathologistEmail: string | null;
  reviewerName: string | null;
  reviewerEmail: string | null;
  cdrFormConfirmedBy: string | null;
  cdrFormConfirmedByEmail: string | null;
}

export interface IPatientC1DataRequest {
  patientId: string;
  eventNumber?: number;
}

export interface IPatientC1DataResult extends IPatientC1Data, IPatientC1DataRequest {
  failed?: boolean;
}

export interface IPatientDemographics extends IConfirmedDiagnosisResp {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  treatingOncologist: string;
  // Clinical One email addresses for the clinicians a report is sent to. Null when
  // C1 has no address, or holds an 'Unlisted' sentinel with no free-text fallback.
  treatingOncologistEmail?: string | null;
  pathologistEmail?: string | null;
  site: string;
  age?: number;
  dateOfEnrolment?: string;
  enrolmentOncologist?: string | null;
  dateOfDeath?: string | null;
  germlineConsent?: boolean | null;
  events?: IEvent[];
  category1Consent?: boolean;
  category2Consent?: boolean;
}

export interface IPatientDetails extends IPatientDemographics {
  patientId: string;
  sex: string;
  vitalStatus: string;
  hospital: string;
  ageAtDiagnosis: string;
  ageAtDeath: string | null;
  enrolmentDate: string;
}

export interface IAccessiblePatient {
  patientId?: string;
  analysisSetId?: string;
  biosampleId?: string;
  isReadOnly?: boolean;
  isFullCaseAccess?: boolean;
}

export interface IAccessiblePatientQuery {
  patientId?: string;
  analysisSetId?: string;
  biosampleId?: string;
}

export class AccessiblePatientQueryDTO implements IAccessiblePatientQuery {
  @IsOptional()
  @IsString()
    patientId?: string;

  @IsOptional()
  @IsString()
    analysisSetId?: string;

  @IsOptional()
  @IsString()
    biosampleId?: string;
}

export type PatientGermlineConsent = Pick<IPatientDemographics, 'germlineConsent' | 'category1Consent' | 'category2Consent'>
